// Blood flow › 3D flow: ba-blood's lattice Boltzmann CFD through IXI 165's own lumen, drawn by the viewport's
// `particles` (its recorded tracers) and `surface:wall` (the lumen wall lit by a field) layers for the run in the
// `cfd` slot (viewport-api-v0, "A 3D CFD"; engines/wgpu #815). The view drives the slot itself: the scene's 1D runs
// stand aside while it's on (ScenePreset.aside), the pack's arteries are hidden by id (ScenePreset.hideSystems),
// the CFD's lumen standing in for them, and the scene's clock plays the cycle. The run id is CFD_RUN, in one place
// (scenarios/index.ts): ba-engine's display copy, about 40 MB against 267 MB a cycle, replaces it there when ba-blood
// confirms its id.

import { useEffect } from 'react';
import { create } from 'zustand';
import { useAtlas, type AtlasState } from '../app/store.ts';
import { CFD_DISPLAY_RUN, CFD_RUN } from '../scenarios/index.ts';
import { defaultSource } from '../data/sims/files.ts';
import { drawsSurfaceLayer } from '../app/scene.ts';
import { getViewport } from '../viewport/host.ts';
import type { Command } from '../viewport/api.ts';

export const cfdActive = (s: Pick<AtlasState, 'scenario' | 'preset'>): boolean => s.scenario === 'bloodflow' && s.preset === 'cfd';

/** The viewport slot the view loads the run into: its own, beside the scene's `main`, which the bridge never touches. */
export const CFD_SLOT = 'cfd';

/** The wall's fields, as the run records them (hemo-sim-v0): the engine's `quantity`, the panel's names and units. */
export const WALLS = [
  { id: 'wss', name: 'Wall shear stress', short: 'Shear', units: 'Pa', perFrame: true },
  { id: 'tawss', name: 'Time-averaged wall shear stress', short: 'Mean', units: 'Pa', perFrame: false },
  { id: 'osi', name: 'Oscillatory shear index', short: 'OSI', units: '', perFrame: false },
  { id: 'pressure', name: 'Cycle-mean pressure at the wall', short: 'Pressure', units: 'mmHg', perFrame: false },
] as const;
export type WallField = (typeof WALLS)[number]['id'];
export const isWall = (v: string | null | undefined): v is WallField => WALLS.some((w) => w.id === v);
export const DEFAULT_WALL: WallField = 'wss';
export const wallOf = (id: WallField) => WALLS.find((w) => w.id === id)!;

/** Tracers drawn at most, and by default: the 20,000 of the run's 100,000 the engine draws, every 5th, and all the
 *  display copy will hold, so the view never leans on the 80,000 it doesn't draw. */
export const TRACERS_MAX = 20000;
export const TRACERS_STEP = 1000;
export const DEFAULT_TRACERS = TRACERS_MAX;

/** The engine's shear scale, the film's cyan glow added over the red membrane (cinematic/cfd-hero/wall.py). */
export const SHEAR_STOPS: [number, string][] = [
  [0, '#000000'],
  [0.25, '#04243E'],
  [0.5, '#116D9A'],
  [0.75, '#58BCE2'],
  [1, '#F0FDFF'],
];
export const shearGradient = `linear-gradient(to right, ${SHEAR_STOPS.map(([t, c]) => `${c} ${Math.round(t * 100)}%`).join(', ')})`;

/** ba-blood's Methods lines, verbatim: the full run's, and the display copy's (#2, 10:20 UTC 10-03); the one for CFD_RUN. */
const FULL_METHODS = "Pulsatile 3D lattice Boltzmann flow through IXI-165's TOF-MRA lumen, driven by openBF v4; advected tracers and wall shear stress.";
const DISPLAY_METHODS =
  "Pulsatile 3D lattice Boltzmann flow through IXI-165's TOF-MRA lumen, driven by openBF v4; 20,000 recorded tracers and wall shear stress. Display copy uses u16 quantization; the source simulation and measured display errors are linked in the header.";
export const CFD_METHODS = CFD_RUN === CFD_DISPLAY_RUN ? DISPLAY_METHODS : FULL_METHODS;
/** The caption under the viewport: model · data. */
export const CFD_CAPTION = '3D lattice Boltzmann flow · IXI 165’s own lumen from TOF-MRA';

/** What the run's header says (hemo-sim-v0, kind 3d-cfd), as far as Methods reads it. */
export interface CfdHeader {
  id: string;
  version?: string;
  kind?: string;
  methods?: string;
  provenance?: string;
  space?: string;
  pack?: { id: string; version?: string };
  model?: { name?: string; version?: string; equations?: string; lattice?: { dx_mm?: number; dt_s?: number; nodes?: number }; blood?: { rho_kg_m3?: number; mu_Pa_s?: number; rheology?: string }; outlets?: string; wss?: string; citation?: string };
  time?: { period_s?: number; frames?: number; dt_s?: number };
  inputs?: { what: string; provenance?: string; source?: string }[];
  units?: Record<string, string>;
  fields?: Record<string, { quantity?: string; units?: string; layout?: string }>;
  validation?: { summary?: string; report?: string };
  pipeline?: { path?: string; commit?: string };
  /** A display copy's note (hemo-sim-v0 "Display copies"): what it keeps, and the run it is a copy of. */
  display?: { version?: string; what?: string; of?: { id: string; version?: string; header_sha256?: string }; left_out?: string[] };
}

export type CfdStatus = 'idle' | 'loading' | 'on' | 'off' | 'unsupported' | 'error';

export interface CfdStore {
  wall: WallField;
  tracers: number;
  tracersOn: boolean;
  /** The slot in the viewport: loading its run, on, off, a viewport without surface layers, or refused (why). */
  status: CfdStatus;
  why: string | null;
  header: CfdHeader | null;
  /** The scale each field draws on, by the engine's rule from the wall's own fields (fetchRanges); the engine's documented
   *  45.7 Pa for wss until then. */
  ranges: Partial<Record<WallField, [number, number]>>;
  /** What the viewport said of the run when it loaded, if anything (frames, tracers stored). */
  loaded: { frames?: number; tracers?: number } | null;
  setWall(w: WallField): void;
  setTracers(n: number): void;
  setTracersOn(on: boolean): void;
}

const INITIAL = { wall: DEFAULT_WALL, tracers: DEFAULT_TRACERS, tracersOn: true, status: 'idle' as CfdStatus, why: null, header: null, ranges: { wss: [0, 45.7] as [number, number] }, loaded: null };

export const useCfd = create<CfdStore>()((set) => ({
  ...INITIAL,
  setWall: (wall) => set({ wall }),
  setTracers: (n) => set({ tracers: Math.round(Math.min(TRACERS_MAX, Math.max(TRACERS_STEP, n)) / TRACERS_STEP) * TRACERS_STEP }),
  setTracersOn: (tracersOn) => set({ tracersOn }),
}));

const reason = (e: unknown): string => (e instanceof Error ? e.message : String(e));

/** The layer commands for the view's state: the wall lit by its field, the tracers on or off with their count. */
export function cfdLayers(s: Pick<CfdStore, 'wall' | 'tracers' | 'tracersOn'>): Command[] {
  return [
    { cmd: 'layer', name: 'surface:wall', on: true, slot: CFD_SLOT, quantity: s.wall },
    { cmd: 'layer', name: 'particles', on: s.tracersOn, slot: CFD_SLOT, ...(s.tracersOn ? { count: s.tracers } : {}) },
  ];
}

// One drive at a time: a start or stop that an later one overtakes gives up at its next step.
let gen = 0;

async function start(): Promise<void> {
  const my = ++gen;
  useCfd.setState({ status: 'loading', why: null });
  const vp = await getViewport();
  if (!vp || my !== gen) return;
  try {
    const r = (await vp.send({ cmd: 'sim', path: CFD_RUN, slot: CFD_SLOT })) as { frames?: number; tracers?: number } | undefined;
    if (my !== gen) return;
    useCfd.setState({ loaded: r && typeof r === 'object' ? { ...(typeof r.frames === 'number' ? { frames: r.frames } : {}), ...(typeof r.tracers === 'number' ? { tracers: r.tracers } : {}) } : {} });
    await sendLayers(my);
    if (my !== gen) return;
    useCfd.setState({ status: 'on' });
  } catch (e) {
    if (my !== gen) return;
    useCfd.setState({ status: 'error', why: reason(e) });
  }
}

async function sendLayers(my = gen): Promise<void> {
  const vp = await getViewport();
  if (!vp || my !== gen) return;
  const st = useCfd.getState();
  for (const c of cfdLayers(st)) {
    const r = (await vp.send(c)) as { range?: unknown } | undefined;
    if (my !== gen) return;
    if (c.cmd === 'layer' && c.name === 'surface:wall' && r && Array.isArray(r.range) && r.range.length === 2 && r.range.every((v) => typeof v === 'number')) {
      useCfd.setState((s) => ({ ranges: { ...s.ranges, [st.wall]: r.range as [number, number] } }));
    }
  }
}

async function stop(): Promise<void> {
  const my = ++gen;
  const was = useCfd.getState().status;
  useCfd.setState({ status: 'off', why: null });
  if (was !== 'on' && was !== 'loading') return;
  const vp = await getViewport();
  if (!vp || my !== gen) return;
  try {
    await vp.send({ cmd: 'layer', name: 'particles', on: false, slot: CFD_SLOT });
    await vp.send({ cmd: 'layer', name: 'surface:wall', on: false, slot: CFD_SLOT });
    await vp.send({ cmd: 'sim', path: null, slot: CFD_SLOT });
  } catch {
    // A viewport that never took the run has nothing to forget.
  }
}

/** The p-th percentile of a sample (nearest rank), ignoring NaN. */
function percentile(xs: Float32Array, p: number): number {
  const a = Array.from(xs).filter((v) => Number.isFinite(v)).sort((x, y) => x - y);
  if (!a.length) return NaN;
  return a[Math.min(a.length - 1, Math.max(0, Math.round((p / 100) * (a.length - 1))))]!;
}

/** Three significant figures, for a scale's end. */
const sig3 = (x: number) => Number(x.toPrecision(3));

let rangesFetched = false;
/** The scales the engine draws each field on, by its own rule (viewport-api-v0, "A 3D CFD"): a per-frame field from 0 to 1.75 ×
 *  the 98th percentile of its time average, a field of one frame from 0 to its 98th percentile, pressure from its 2nd to its
 *  98th. Computed here from the wall's own per-vertex fields (about 1 MB each), so the legend reads what the wall shows; the
 *  engine's reply carries no range. Left as they were where a file is missing (the display copy may carry other fields). */
async function fetchRanges(): Promise<void> {
  if (rangesFetched) return;
  rangesFetched = true;
  const src = defaultSource();
  const field = async (name: string): Promise<Float32Array | null> => {
    try {
      const r = await src.fetch(`${src.base}${CFD_RUN}/surfaces/wall.${name}.f32`);
      if (!r.ok) return null;
      return new Float32Array(await r.arrayBuffer());
    } catch {
      return null;
    }
  };
  const [tawss, osi, pressure] = await Promise.all([field('tawss'), field('osi'), field('pressure')]);
  const out: Partial<Record<WallField, [number, number]>> = {};
  if (tawss) {
    const p98 = percentile(tawss, 98);
    if (Number.isFinite(p98)) {
      out.wss = [0, sig3(1.75 * p98)];
      out.tawss = [0, sig3(p98)];
    }
  }
  if (osi) {
    const p98 = percentile(osi, 98);
    if (Number.isFinite(p98)) out.osi = [0, sig3(p98)];
  }
  if (pressure) {
    const lo = percentile(pressure, 2);
    const hi = percentile(pressure, 98);
    if (Number.isFinite(lo) && Number.isFinite(hi)) out.pressure = [sig3(lo), sig3(hi)];
  }
  if (Object.keys(out).length) useCfd.setState((s) => ({ ranges: { ...s.ranges, ...out } }));
}

let headerFetched = false;
async function fetchHeader(): Promise<void> {
  if (headerFetched) return;
  headerFetched = true;
  try {
    const src = defaultSource();
    const r = await src.fetch(`${src.base}${CFD_RUN}/header.json`);
    if (!r.ok) throw new Error(`${r.status} for ${CFD_RUN}/header.json`);
    useCfd.setState({ header: (await r.json()) as CfdHeader });
  } catch (e) {
    headerFetched = false;
    useCfd.setState((s) => (s.why ? s : { why: `The run’s header didn’t load: ${reason(e)}` }));
  }
}

/** Whether this viewport draws a CFD's lumen: not the 2D preview, and `ready.layers` lists the surface family (app/scene.ts). */
export const cfdSupported = (s: Pick<AtlasState, 'engine'>): boolean => drawsSurfaceLayer(s.engine, 'surface:wall');

/** Drives the slot while the view is on and the pack has loaded: the run in, its layers following the panel; out when the
 *  view goes. Mounted once, by the panel's sections (features/Cfd.tsx), which stay mounted with the panel. */
export function useCfdDrive(): void {
  const active = useAtlas(cfdActive);
  const ready = useAtlas((s) => s.engine.status === 'ready' && s.load.status === 'ready');
  const supported = useAtlas(cfdSupported);
  const wall = useCfd((s) => s.wall);
  const tracers = useCfd((s) => s.tracers);
  const tracersOn = useCfd((s) => s.tracersOn);
  useEffect(() => {
    if (!active) return;
    void fetchHeader();
    void fetchRanges();
    if (!ready) return;
    if (!supported) {
      useCfd.setState({ status: 'unsupported', why: useAtlas.getState().engine.kind === 'mock' ? 'The 2D preview doesn’t draw a CFD’s lumen: 3D flow needs the 3D viewport.' : 'This viewport doesn’t draw a CFD’s lumen: its build lists no surface layers (before engines/wgpu #815).' });
      return;
    }
    void start();
    return () => void stop();
  }, [active, ready, supported]);
  useEffect(() => {
    if (active && useCfd.getState().status === 'on') void sendLayers();
  }, [active, wall, tracers, tracersOn]);
}

// The store for probes and the e2e run, as dfc.ts and text.ts expose theirs.
if (typeof window !== 'undefined') (window as unknown as { __cfd?: typeof useCfd }).__cfd = useCfd;
